// Stone: heavy ball that falls when the game starts (tips the seesaw, pulls the pulley)
import { definePart } from '../core/registry';
import { Physics } from '../core/physics';
import { shadowOn, shadowOff, C, DEG } from '../core/draw-helpers';
import type { Ctx2D, GameBody, PhysicsApi } from '../core/types';

export const STONE_R = 24;

export function drawStone(ctx: Ctx2D, x: number, y: number, a: number, sel?: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  shadowOn(ctx, 10, 6);
  ctx.beginPath();
  for (let i = 0; i < 9; i++) { const t = i / 9 * Math.PI * 2, r = 24 + Math.sin(i * 2.3) * 2.2; ctx.lineTo(Math.cos(t) * r, Math.sin(t) * r); }
  ctx.closePath();
  const g = ctx.createRadialGradient(-8, -10, 4, 0, 0, 28); g.addColorStop(0, '#E9E5F2'); g.addColorStop(1, C.stone2);
  ctx.fillStyle = g; ctx.fill(); shadowOff(ctx);
  ctx.fillStyle = 'rgba(80,70,100,.3)';
  [[6, 6, 4], [-8, 8, 3], [9, -8, 2.5]].forEach(([px, py, r]) => { ctx.beginPath(); ctx.arc(px, py, r, 0, 7); ctx.fill(); });
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 30, 0, 7); ctx.strokeStyle = '#fff'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}

/** The stone's body (also used by the rope when a stone hangs from it) */
export function stoneBody(api: PhysicsApi, x: number, y: number, label?: string): GameBody {
  const b = api.add(api.Bodies.circle(x, y, STONE_R, Object.assign({ density: 0.015, friction: 0.3, restitution: 0.1, frictionAir: 0.001 }, label ? { label } : {}))) as GameBody;
  b.kind = 'stone';
  return b;
}

export const stone = definePart({
  circle: STONE_R, rot: false, thumb: false,
  // the player's stones are drawn as a placement in the build phase, then as a body
  placement: 'build',
  create(api, pl, { fixed, idx }) {
    const b = stoneBody(api, pl.x, pl.y, 'stone');
    b.partIdx = idx; b.fixedPart = fixed; api.S.dyn.push(b);
  },
  draw: (ctx, pl, sel) => drawStone(ctx, pl.x, pl.y, 0, sel),
  icon: ctx => drawStone(ctx, 0, 0, 0),
  // fall line in the build phase
  guide(ctx, pl) {
    ctx.save(); ctx.setLineDash([4, 10]); ctx.lineCap = 'round'; ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(59,42,92,.25)';
    ctx.beginPath(); ctx.moveTo(pl.x, pl.y + 30); ctx.lineTo(pl.x, Physics.H); ctx.stroke(); ctx.restore();
  },
  art: {
    stone: {
      layer: 'over', thumb: 5,
      draw: (ctx, b) => drawStone(ctx, b.position.x, b.position.y, b.angle),
      reskin: b => ({ t: 'stone', x: b.position.x, y: b.position.y, a: b.angle / DEG, body: b }),
      // in the build phase only stones that are not a placement (e.g. on a rope)
      inBuild: b => b.partIdx == null,
    },
  },
  system: {
    // heavy sound when the stone hits something
    strike: { stone: (api, b, other) => { if (!other.isSensor && b.speed > 4) api.S.events.push({ e: 'thunk', x: b.position.x, y: b.position.y, v: b.speed }); } },
  },
});
